import {useCallback, useEffect, useState} from 'react';
import {TabGroup, TabList, TabPanel, TabPanels} from '@headlessui/react';
import CheckIcon from '@/assets/images/icon-check.svg?react';
import {useDocumentContext} from '@/components/document/context';
import Message from '@/components/message';
import Spinner from '@/components/spinner';
import {TrackedTab} from '@/components/tracked-atoms/tracked-tab';
import {SUMMARY_SECTION} from '@/constants/sections';
import {analyticsHandler} from '@/services/analytics/analytics-handler';
import {WEB_CLICK_TRACKING_KEY} from '@/services/analytics/web-click-tracking-key';
import {useDocumentLocale} from '../hooks';
import {PdfVisualizer} from './pdf-visualizer';
import {useDocumentForms} from './use-document-forms';

export const DocumentSummary = () => {
  const {document, setSectionValidity} = useDocumentContext();
  const forms = useDocumentForms();
  const [visited, setVisited] = useState<string[]>([]);
  const {t} = useDocumentLocale();

  // When all documents are visited, mark the summary section as valid
  const allVisited = forms.every(doc => doc && visited.includes(doc.type));
  const isLoading = forms.some(doc => doc.query.isLoading);

  useEffect(() => {
    if (allVisited && !isLoading) {
      setSectionValidity(SUMMARY_SECTION, true);
    } else {
      setSectionValidity(SUMMARY_SECTION, false);
    }
  }, [allVisited, setSectionValidity, isLoading]);

  // Mark a document as visited on embed load event, all document must be visited before the summary section is valid
  const onLoaded = useCallback(
    (type: string) => {
      setVisited(visited => [...visited, type]);
      analyticsHandler.webViewContent({
        content_name: type,
        content_detail: null,
      });
    },
    [setVisited],
  );

  return (
    <>
      {forms.length === 0 && (
        <div className="mx-auto flex size-full max-w-3xl items-center justify-center text-center">
          <Message dataTestid="no-forms" text={t('components.summary.no-forms-description')} />
        </div>
      )}

      {forms.length > 1 && !allVisited && (
        <div className="mx-auto my-8 flex size-full max-w-3xl items-center justify-center text-center">
          <Message dataTestid="multiple-forms" text={t('components.summary.multiple-forms-instructions')} />
        </div>
      )}

      <TabGroup className="flex flex-row gap-8" vertical>
        {forms.length > 1 ? (
          <TabList className="flex w-52 flex-col gap-2">
            {forms.map(({type, query}) => (
              <TrackedTab
                key={type}
                className={
                  'flex flex-row items-center justify-between rounded bg-violet-50 px-3 py-2 font-mono text-purple-925 transition-colors hover:bg-violet-100 data-[selected]:bg-violet data-[selected]:text-white'
                }
                aria-label={t('components.summary.document.accessibility-button', {documentType: type})}
                trackingKey={WEB_CLICK_TRACKING_KEY.VIEW_DOCUMENT_SUMMARY}
              >
                {({selected}) => (
                  <>
                    <div>{type}</div>
                    <div className="flex flex-row items-center justify-center gap-2">
                      {query.isLoading ? <Spinner thin inverted={selected} className={'size-4'} /> : ''}
                      {query.data && visited.includes(type) ? <CheckIcon className="size-4 fill-current" /> : ''}
                    </div>
                  </>
                )}
              </TrackedTab>
            ))}
          </TabList>
        ) : null}

        <TabPanels className={'flex flex-1'}>
          {forms.map(({type, query: {data, isFetching, isLoading}}) => (
            <TabPanel className={'flex flex-1'} key={type}>
              {!(isFetching || isLoading) && data ? (
                <PdfVisualizer documentId={document.id} form={data} secure={true} onLoaded={onLoaded} />
              ) : (
                <div className="flex min-h-document-content-min-height w-full items-center justify-center">
                  <Spinner />
                </div>
              )}
            </TabPanel>
          ))}
        </TabPanels>
      </TabGroup>
    </>
  );
};
